// SISTER FILE: frontend/src/pages/TrailheadCore.tsx
// PersonaChat is the boring-and-proven daily driver. Trailhead's chat-surface
// is a sister-file replica at TrailheadCore.tsx with TrailheadLayout chrome
// wrapping the same PersonaConversationSurface shell. Bug fixes to the chat
// surface land in the shared shell, not here. This orchestrator only owns
// /chat/* routing and v0.4 lifted-state plumbing.

import React, { useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ConversationProvider } from '../contexts/ConversationContext';
import { CouncilProvider } from '../contexts/CouncilContext';
import { LensProvider } from '../contexts/LensContext';
import { PersonaChatAdapter } from '../adapters/personaChatAdapter';
import { LiveProgressPipe } from '../components/conversation/internals/LiveProgressPipe';
import { ScrollObserver } from '../components/conversation/internals/ScrollObserver';
import { PersonaConversationSurface } from '../components/conversation/PersonaConversationSurface';
import { EMPTY_PHASE_EVENTS } from '../contexts/ChatDagRuntimeContext';
import { useTheme } from '../contexts/ThemeContext';
import { SkillPicker } from '../components/skills/SkillPicker';
import { useSkillsForSession, useAttachToSession } from '../hooks/useSkills';
import { usePersonaSessionResolution } from '../hooks/usePersonaSessionResolution';
import type { PhaseEvent } from '../hooks/usePersonaToolProgress';

export const PersonaChat: React.FC = () => {
  const { personaId, sessionId } = useParams<{ personaId?: string; sessionId?: string }>();
  const navigate = useNavigate();
  const { darkMode } = useTheme();
  const [showSkillPicker, setShowSkillPicker] = useState(false);

  // v0.4 keystone — viewport + live-progress lift here so a single
  // <ScrollObserver> + <LiveProgressPipe> serve the entire surface.
  const [viewportTurnId, setViewportTurnId] = useState<string | null>(null);
  const [liveLoadingStatus, setLiveLoadingStatus] = useState<string>('');
  const [livePhaseEvents, setLivePhaseEvents] = useState<PhaseEvent[]>(EMPTY_PHASE_EVENTS);
  const [wsLastEventMs, setWsLastEventMs] = useState<number | null>(null);
  const [inFlightTurnId, setInFlightTurnId] = useState<string | null>(null);
  const [errorTurnId, setErrorTurnId] = useState<string | null>(null);
  const handleLiveProgressUpdate = useCallback(
    (
      status: string,
      events: PhaseEvent[],
      ms: number | null,
      turnId: string | null,
      errId: string | null,
    ) => {
      setLiveLoadingStatus(status);
      setLivePhaseEvents(events);
      setWsLastEventMs(ms);
      setInFlightTurnId(turnId);
      setErrorTurnId(errId);
    },
    [],
  );

  const { persona, session, adapter, newSessionData, isLoading, isPersonaLoading, isSessionLoading, isSessionCreating, personaError } =
    usePersonaSessionResolution({
      personaId,
      sessionId,
      onSessionCreated: (effectivePersonaId, newSessionId) => {
        navigate(`/chat/persona/${effectivePersonaId}/${newSessionId}`, { replace: true });
      },
    });

  const actualSessionIdForSkills = sessionId || newSessionData?.session_id || '';
  const { data: sessionSkills = [] } = useSkillsForSession(actualSessionIdForSkills);
  const attachSkillsMutation = useAttachToSession();

  if (isLoading) {
    return (
      <div className="flex flex-col justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500 mb-4"></div>
        <div className="text-sm text-gray-500">
          {isPersonaLoading ? 'Loading persona...' : isSessionLoading ? 'Loading session...' : isSessionCreating ? 'Creating session...' : 'Loading...'}
        </div>
      </div>
    );
  }

  const isFallback = (personaId === 'unknown' || !personaId) && !!sessionId;

  if (isFallback) {
    const fallbackPersona = persona || { id: 'unknown', name: 'Chat Assistant', shortDescription: 'AI Assistant', tags: [] };
    const fallbackAdapter = adapter ?? new PersonaChatAdapter(fallbackPersona);
    const participants = [
      { id: 'user', name: 'You', type: 'user' as const },
      { id: fallbackPersona.id, name: fallbackPersona.name, type: 'persona' as const, metadata: fallbackPersona },
    ];
    return (
      <>
        <ConversationProvider sessionId={sessionId!} adapter={fallbackAdapter} participants={participants} metadata={{ isNew: false, personaId: fallbackPersona.id }}>
          <CouncilProvider sessionId={sessionId!}>
            <LensProvider sessionId={sessionId!}>
              <ScrollObserver onViewportTurn={setViewportTurnId} />
              <LiveProgressPipe sessionId={sessionId!} onUpdate={handleLiveProgressUpdate} />
              <PersonaConversationSurface
                persona={fallbackPersona} session={session} sessionId={sessionId!} adapter={fallbackAdapter} isFallback
                sessionSkills={sessionSkills} onShowSkillPicker={() => setShowSkillPicker(true)} onBack={() => navigate('/chat/sessions')}
                loadingStatus={liveLoadingStatus} phaseEvents={livePhaseEvents} wsLastEventMs={wsLastEventMs}
                inFlightTurnId={inFlightTurnId} errorTurnId={errorTurnId}
                viewportTurnId={viewportTurnId} setViewportTurnId={setViewportTurnId}
              />
            </LensProvider>
          </CouncilProvider>
        </ConversationProvider>
        <SkillPicker isOpen={showSkillPicker} onClose={() => setShowSkillPicker(false)}
          onAttach={(names) => { if (sessionId) attachSkillsMutation.mutate({ sessionId, skillNames: names }); }}
          currentSkills={sessionSkills.map((s: any) => s.skill_name)} />
      </>
    );
  }

  if (personaError && !persona && !sessionId) {
    console.error('Persona error:', personaError);
    return (
      <div className={`${darkMode ? 'bg-red-900 bg-opacity-20 border-red-800 text-red-300' : 'bg-red-50 border-red-200 text-red-700'} px-4 py-3 rounded relative border`} role="alert">
        <strong className="font-bold">Error!</strong>
        <span className="block sm:inline"> Failed to load persona.</span>
        <button className={`mt-3 ${darkMode ? 'bg-red-800 bg-opacity-50 text-red-200 hover:bg-opacity-70' : 'bg-red-100 text-red-800 hover:bg-red-200'} px-3 py-1 rounded`} onClick={() => navigate('/chat/sessions')}>
          Return to Chats
        </button>
      </div>
    );
  }

  if (!persona) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  const actualSessionId = sessionId || newSessionData?.session_id || 'temp-session-id';
  const participants = [
    { id: 'user', name: 'You', type: 'user' as const },
    { id: persona.id, name: persona.name, type: 'persona' as const, metadata: persona },
  ];

  return (
    <ConversationProvider sessionId={actualSessionId} adapter={adapter!} participants={participants} metadata={{ isNew: !sessionId, personaId: persona.id }}>
      <CouncilProvider sessionId={actualSessionId}>
        <LensProvider sessionId={actualSessionId}>
          <ScrollObserver onViewportTurn={setViewportTurnId} />
          <LiveProgressPipe sessionId={actualSessionId} onUpdate={handleLiveProgressUpdate} />
          <PersonaConversationSurface
            persona={persona} session={session} sessionId={actualSessionId} adapter={adapter!}
            onBack={() => navigate('/chat/sessions')}
            loadingStatus={liveLoadingStatus} phaseEvents={livePhaseEvents} wsLastEventMs={wsLastEventMs}
            inFlightTurnId={inFlightTurnId} errorTurnId={errorTurnId}
            viewportTurnId={viewportTurnId} setViewportTurnId={setViewportTurnId}
          />
        </LensProvider>
      </CouncilProvider>
    </ConversationProvider>
  );
};
